import { useState } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import Small from 'UIComponents/elements/Small';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import AILoadingIconButton from 'ai-components-ui-library/components/buttons/AILoadingIconButton';
import AudioPlayer from 'audio-components/player/AudioPlayer';
import LoadingSkeleton from 'audio-components/player/LoadingSkeleton';
import GenerationDisabledTooltip from 'file-manager-components/image-gen/GenerationDisabledTooltip';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getIsUngatedForBreeze } from '../../redux/selectors/authSelectors';
import styled from 'styled-components';
import { View } from 'foundations-components/components/View';
import { BulbIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FlexGrowContainer = styled.div.withConfig({
  displayName: "AudioPreviewField__FlexGrowContainer",
  componentId: "sc-13eb8l6-0"
})(["flex-grow:1;"]);
const AudioPreviewPlayer = ({
  src,
  isGenerationPending
}) => {
  if (isGenerationPending) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(LoadingSkeleton, {
          "data-test-id": "player-loading"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(BulbIcon, {}), /*#__PURE__*/_jsx(Spacer, {
          display: "inline-block",
          space: "100"
        }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "audioNarration.panel.aiDisclaimer"
        })]
      })]
    });
  }
  return src ? /*#__PURE__*/_jsx(View, {
    marginBlockEnd: "300",
    children: /*#__PURE__*/_jsx(AudioPlayer, {
      fileUrl: src
    })
  }) : null;
};
const AudioPreviewField = ({
  audioUrl,
  file,
  hasChangedSinceLastGeneration,
  isGenerativeAiEnabled,
  isFileLoading,
  isGenerationPending,
  isInputTextEmpty,
  isOverCharLimit,
  isPanelOpenComplete,
  isSaveToFilesPending,
  onClickGenerate,
  onRemove
}) => {
  const isUngatedForBreeze = useSelector(getIsUngatedForBreeze);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  if (isFileLoading || !isPanelOpenComplete) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      className: "m-top-12",
      layout: "centered"
    });
  }
  let src = audioUrl;
  if (!src && file) {
    src = file.url;
  }
  if (!src && !isGenerationPending) {
    return null;
  }
  const generateDisabled = !isGenerativeAiEnabled || !hasChangedSinceLastGeneration || isInputTextEmpty || isOverCharLimit || isGenerationPending;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx(FlexGrowContainer, {
        children: /*#__PURE__*/_jsx(UIFormLabel, {
          htmlFor: "audio_player",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "audioNarration.panel.preview.label"
          })
        })
      }), /*#__PURE__*/_jsx(GenerationDisabledTooltip, {
        contentAssistanceEnabled: isGenerativeAiEnabled,
        children: isUngatedForBreeze ? /*#__PURE__*/_jsx(AILoadingIconButton, {
          "aria-label": I18n.text('audioNarration.panel.generateAgain'),
          featureCategoryId: "blog-editor-regenerate-audio",
          disabled: generateDisabled,
          isRegenerate: true,
          loading: isGenerationPending,
          size: "xs",
          tooltip: generateDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "audioNarration.panel.generateAgainDisabled"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "audioNarration.panel.generateAgain"
          }),
          usageTracker: UsageTracker.getTracker(),
          onClick: onClickGenerate
        }) : /*#__PURE__*/_jsx(UITooltip, {
          disabled: isGenerationPending || hasChangedSinceLastGeneration,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "audioNarration.panel.generateAgainDisabled"
          }),
          children: /*#__PURE__*/_jsx(Small, {
            children: /*#__PURE__*/_jsxs(UILink, {
              disabled: generateDisabled,
              onClick: onClickGenerate,
              children: [/*#__PURE__*/_jsx(UIIcon, {
                name: "refresh"
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "audioNarration.panel.generateAgain"
              })]
            })
          })
        })
      }), file && /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          marginInlineStart: "300",
          children: /*#__PURE__*/_jsx(UILink, {
            disabled: isGenerationPending || isSaveToFilesPending,
            onClick: () => setIsConfirmRemoveOpen(true),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "audioNarration.panel.remove"
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsx(AudioPreviewPlayer, {
      isGenerationPending: isGenerationPending,
      src: src
    }), isConfirmRemoveOpen && /*#__PURE__*/_jsx(UIConfirmModal, {
      confirmUse: "danger",
      use: "danger",
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "audioNarration.confirmRemove.message"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "audioNarration.confirmRemove.description"
      }),
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "audioNarration.confirmRemove.confirmLabel"
      }),
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "audioNarration.confirmRemove.rejectLabel"
      }),
      onConfirm: () => {
        setIsConfirmRemoveOpen(false);
        onRemove();
      },
      onReject: () => setIsConfirmRemoveOpen(false)
    })]
  });
};
export default AudioPreviewField;